import type { WorkspaceEntry, WorkspacePort } from '@crowdex/workspace-protocol';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

/** The preview's repository (D34): a few folders, the files the conversation changed. */
const PREVIEW_FILES: Readonly<Record<string, string>> = {
  'apps/web/src/components/mobile-shell/mobile-shell.tsx': `import { MobileDrawer } from './mobile-drawer';

export function MobileShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  useSwipeBack(() => setDrawerOpen(true));

  return (
    <div className="mobile-shell">
      <MobileDrawer
        open={drawerOpen}
        onNewTask={startTask}
      />
      {dialogs}
    </div>
  );
}
`,
  'apps/web/src/components/mobile-shell/mobile-drawer.tsx': `export function MobileDrawer({ open }: { open: boolean }) {
  return <nav aria-label="Drawer" hidden={!open} />;
}
`,
  'apps/web/src/components/mobile-shell/use-swipe-back.ts': `export function useSwipeBack(onBack: () => void) {
  // A swipe from the left edge goes back.
}
`,
  'apps/web/src/components/mobile-shell/activity-model.ts': 'export const activity = [];\n',
  'apps/web/src/components/settings-page/settings-page.tsx': 'export function SettingsPage() {}\n',
  'package.json': '{\n  "name": "crowdex"\n}\n',
};

/** The preview's servers (D38). */
export const PREVIEW_LOCAL_PORTS: readonly WorkspacePort[] = [
  { port: 5173, process: 'vite · pnpm dev' },
  { port: 3000, process: 'node · pnpm --filter web serve' },
];

function entriesOf(directory: string): WorkspaceEntry[] {
  const prefix = directory === '' ? '' : `${directory}/`;
  const names = new Map<string, boolean>();
  for (const path of Object.keys(PREVIEW_FILES)) {
    if (!path.startsWith(prefix)) continue;
    const [name, ...rest] = path.slice(prefix.length).split('/');
    if (name !== undefined) names.set(name, rest.length > 0 || names.get(name) === true);
  }
  return [...names]
    .sort(([a, aDir], [b, bDir]) => Number(bDir) - Number(aDir) || a.localeCompare(b))
    .map(([name, isDirectory]) => ({ name, path: `${prefix}${name}`, isDirectory }));
}

function unavailable(): never {
  throw new Error('The preview has no workspace.');
}

/** Lists and reads the preview's files; anything else is not there. */
export const PREVIEW_FILES_CLIENT: WorkspaceClient = {
  ready: Promise.resolve(),
  listDirectory: async (path) => entriesOf(path),
  readTextFile: async (path) => PREVIEW_FILES[path] ?? unavailable(),
  listPorts: async () => [],
  readBinaryFile: unavailable,
  writeBinaryFile: unavailable,
  writeTextFile: unavailable,
  copyFile: unavailable,
  removeFile: unavailable,
  gitReview: unavailable,
  checkpointDiff: unavailable,
  revertCheckpoints: unavailable,
  updateGitFile: unavailable,
  spawnTerminal: unavailable,
  writeTerminal: unavailable,
  resizeTerminal: unavailable,
  killTerminal: unavailable,
  close: () => undefined,
};
